import type {FormPackingTables} from '@/views/PackingTables/components/PackingTablesForm/PackingTablesForm.types';
import type {
  GetPrinterGroupsResponse,
  GetPrintersResponse,
  PostCreatePrinterGroupData,
} from '@myparcel-frontend/printing-service';
import type {Paper} from '@myparcel-frontend/types';
import {usePrintingService} from '@myparcel-frontend/printing-service';
import {string} from '@myparcel-frontend/utils';
import {xor} from 'es-toolkit/array';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import useUiSettings from '../useUiSettings';
import {useDirectPrintingStore} from '@/store/directPrinting/directPrintingStore';

export const useDirectPrinting = () => {
  const {
    postEnableDirectPrinting,
    getStatus: getStatusService,
    getPrinters: getPrintersService,
    postSyncPrinters,
    postRemovePrinterGroup,
    getPrinterGroups: getPrinterGroupsService,
    postCreatePrinterGroup,
    postUnassignPrinterFromGroup,
    postAssignPrinterToGroup,
    postRenamePrinterGroup,
    postAllowShopsAccessToPrinterGroup,
  } = usePrintingService();
  const {uiSettings} = useUiSettings();
  const directPrintingStore = useDirectPrintingStore();
  const {printerGroups, printers, status, pendingPrintJobs} = storeToRefs(directPrintingStore);

  const packingTableIsActivated = computed(() => status.value === 'ACTIVE');
  const statusIsActiveOrInvalid = computed(
    () => status.value === 'INITIALIZATION_REQUIRED' || status.value === 'ACTIVE' || status.value === 'SUSPENDED',
  );

  const directPrintingIsActive = computed(() => {
    return packingTableIsActivated.value && Boolean(uiSettings.value?.print_label_directly);
  });

  const getStatus = async () => {
    const response = await getStatusService();
    status.value = response.status;
  };

  const getPrinters = async () => {
    if (printers.value.length) {
      return printers.value;
    }

    const {results} = await getPrintersService();
    printers.value = results;

    return results;
  };

  const getPrinterById = (id: string) => {
    return printers.value.find((printer) => printer.id === id);
  };

  const getPrinterGroups = async () => {
    if (printerGroups.value.length) {
      return printerGroups.value;
    }

    const {results} = await getPrinterGroupsService();
    printerGroups.value = results;

    return results;
  };

  const getPrinterName = (id: string) => {
    const printer = getPrinterById(id);

    return printer?.name;
  };

  const isUniquePrinterGroupName = async (name: string) => {
    const printerGroupArray = await getPrinterGroups();

    return !printerGroupArray.find((group) => group.name === name);
  };

  const createPrinterGroup = async (printerGroupData: NonNullable<PostCreatePrinterGroupData['body']>) => {
    const {name, shopIds, a4PrinterId, a6PrinterId} = printerGroupData;

    const printerGroup = await postCreatePrinterGroup({
      name,
      shopIds,
      ...(a4PrinterId && {
        a4PrinterId,
      }),
      ...(a6PrinterId && {
        a6PrinterId,
      }),
    });
    printerGroups.value.push(printerGroup);
  };

  const groupPrintersByFormat = async () => {
    await postSyncPrinters();

    const {results} = await getPrintersService();

    return results.reduce<{
      a4Printers: GetPrintersResponse['results'];
      a6Printers: GetPrintersResponse['results'];
    }>(
      (acc, result) => {
        if (result.formats.includes('A4')) {
          acc.a4Printers.push(result);
        }

        if (result.formats.includes('A6')) {
          acc.a6Printers.push(result);
        }

        return acc;
      },
      {a4Printers: [], a6Printers: []},
    );
  };

  const duplicatePrinterGroup = async (packingTable: GetPrinterGroupsResponse['results'][number], prefix: string) => {
    const duplicatedName = string.duplicate(
      packingTable.name,
      printerGroups.value.map((group) => group.name),
      // TODO: MST-1023 get truncate length from Zod schema
      {
        truncateLength: 255,
        prefix: `${prefix} `,
      },
    );

    const {id, ...duplicatedPrinterGroup} = packingTable;

    await createPrinterGroup({...duplicatedPrinterGroup, name: duplicatedName});
  };

  const removeMultiplePrinterGroups = async (packingTables: GetPrinterGroupsResponse['results']) => {
    const results = await Promise.allSettled(
      packingTables.map((packingTable) => {
        return postRemovePrinterGroup({
          printerGroupId: packingTable.id,
        })
          .then(() => packingTable)
          .catch((error) => {
            const customError = new Error(error.message);
            // @ts-expect-error - API does not return the failed packingTables
            customError.packingTable = packingTable;
            throw customError;
          });
      }),
    );

    const fulfilled = results.filter((result) => result.status === 'fulfilled');
    const rejected = results.filter((result) => result.status === 'rejected');

    const rejectedPackingTables = rejected.map((response) => response.reason.packingTable);

    fulfilled.forEach((response) => {
      directPrintingStore.removePrinterGroup(response.value);
    });

    return {rejectedPackingTables};
  };

  const getPrinterGroupById = async (id: string) => {
    // Endpoint to fetch a single printer-group does not exists. We fetch everything when Pinia is empty and the user directly navigates to the edit page
    if (!printerGroups.value.length) {
      await getPrinterGroups();
    }

    const printerGroup = printerGroups.value.find((printerGroup) => printerGroup.id === id);

    if (!printerGroup) {
      throw new Error('PrinterGroup is not defined');
    }

    return printerGroup;
  };

  const updatePrinter = async ({
    printerGroup,
    newPrinterId,
    format,
  }: {
    printerGroup: GetPrinterGroupsResponse['results'][number];
    newPrinterId?: string;
    format: Paper;
  }) => {
    const containsPrinter = format === 'A4' ? printerGroup.a4PrinterId : printerGroup.a6PrinterId;
    const shouldAddPrinter = !!newPrinterId;

    if (containsPrinter) {
      await postUnassignPrinterFromGroup({
        printerGroupId: printerGroup.id,
        format,
      });
    }

    if (shouldAddPrinter) {
      await postAssignPrinterToGroup({
        printerGroupId: printerGroup.id,
        printerId: newPrinterId,
        format,
      });
    }
  };

  const updatePrinterGroup = async ({
    printerGroup,
    newPrinterGroup,
  }: {
    printerGroup: GetPrinterGroupsResponse['results'][number];
    newPrinterGroup: FormPackingTables;
  }) => {
    const apiCalls = [];

    const shouldUpdateName = printerGroup.name !== newPrinterGroup.name;

    const differentShopIds = xor(printerGroup.shopIds, newPrinterGroup.shopIds);
    const shouldUpdateShops = differentShopIds.length > 0;
    const shouldUpdateA4Printer = printerGroup.a4PrinterId !== newPrinterGroup.a4PrinterId;
    const shouldUpdateA6Printer = printerGroup.a6PrinterId !== newPrinterGroup.a6PrinterId;

    if (shouldUpdateName) {
      apiCalls.push(
        postRenamePrinterGroup({
          printerGroupId: printerGroup.id,
          name: newPrinterGroup.name,
        }).then(() => {
          directPrintingStore.updatePrinterGroup(printerGroup.id, {
            name: newPrinterGroup.name,
          });
        }),
      );
    }

    if (shouldUpdateShops) {
      apiCalls.push(
        postAllowShopsAccessToPrinterGroup({
          printerGroupId: printerGroup.id,
          shopIds: newPrinterGroup.shopIds,
        }).then(() => {
          directPrintingStore.updatePrinterGroup(printerGroup.id, {
            shopIds: newPrinterGroup.shopIds,
          });
        }),
      );
    }

    if (shouldUpdateA4Printer) {
      apiCalls.push(
        updatePrinter({
          printerGroup,
          newPrinterId: newPrinterGroup.a4PrinterId,
          format: 'A4',
        }).then(() => {
          directPrintingStore.updatePrinterGroup(printerGroup.id, {
            a4PrinterId: newPrinterGroup.a4PrinterId,
          });
        }),
      );
    }

    if (shouldUpdateA6Printer) {
      apiCalls.push(
        updatePrinter({
          printerGroup,
          newPrinterId: newPrinterGroup.a6PrinterId,
          format: 'A6',
        }).then(() => {
          directPrintingStore.updatePrinterGroup(printerGroup.id, {
            a6PrinterId: newPrinterGroup.a6PrinterId,
          });
        }),
      );
    }

    await Promise.all(apiCalls);
  };

  const removePrinterGroup = async (packingTable: GetPrinterGroupsResponse['results'][number]) => {
    await postRemovePrinterGroup({
      printerGroupId: packingTable.id,
    });
    directPrintingStore.removePrinterGroup(packingTable);
  };

  const getAllPrinterGroupsById = (printerGroupIds: string[]): GetPrinterGroupsResponse['results'] => {
    const printerGroupIdList = new Set(printerGroupIds);

    return printerGroups.value.filter((printerGroup) => printerGroupIdList.has(printerGroup.id));
  };

  return {
    status,
    printerGroups,
    packingTableIsActivated,
    statusIsActiveOrInvalid,
    directPrintingIsActive,
    pendingPrintJobs,
    duplicatePrinterGroup,
    postEnableDirectPrinting,
    getAllPrinterGroupsById,
    createPrinterGroup,
    getStatus,
    getPrinters,
    getPrinterById,
    getPrinterGroups,
    getPrinterName,
    groupPrintersByFormat,
    isUniquePrinterGroupName,
    removePrinterGroup,
    removeMultiplePrinterGroups,
    getPrinterGroupById,
    updatePrinterGroup,
  };
};
